import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTIONS = [
  { key: 'q1', text: 'What do you truly want, and why does it matter?' },
  { key: 'q2', text: 'What challenges are holding you back?' },
  { key: 'q3', text: 'Are you lacking of opportunity? Lacking of energy?' },
  { key: 'q4', text: 'What part of life do you need Master Steven to consult? Please comment.' },
  { key: 'q5', text: 'Describe one of the issues that you want Master Steven to advice.' },
  { key: 'q6', text: 'If you get the advice and tools, would you follow? Why? Please comment.' }
];

export default function ApplicationForm() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const tokenCode = searchParams.get('t') || '';
  const prefillName = searchParams.get('n') || '';
  const prefillPhone = searchParams.get('p') || '';
  const prefillEmail = searchParams.get('e') || '';

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [formData, setFormData] = useState({ name: prefillName, phone: prefillPhone, email: prefillEmail, q1: '', q2: '', q3: '', q4: '', q5: '', q6: '' });
  const [hasPrefill, setHasPrefill] = useState(!!(prefillName || prefillPhone || prefillEmail));
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/events/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { setEvent(data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
    if (tokenCode && !hasPrefill) {
      fetch(`${API_URL}/api/tokens/${tokenCode}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) { setFormData(prev => ({ ...prev, name: data.name, phone: data.phone, email: data.email })); setHasPrefill(true); } });
    }
  }, [slug, tokenCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/events/${slug}/application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.detail || 'Something went wrong.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center"><p className="text-white">Loading...</p></div>;

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Not Found</h1>
          <p className="text-gray-400">This form link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-[#F5A623]/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-[#F5A623]" />
            </div>
          </div>
          <h1 data-testid="application-success-title" className="text-3xl font-black text-white mb-4">Application Received!</h1>
          <p className="text-gray-400">Thank you for submitting your application. Our team will review and get back to you soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        <div className="bg-[#1A1A1D] border border-white/10 rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#F5A623] to-[#E59400] p-6 text-center">
            <h1 className="text-2xl sm:text-3xl font-black text-black mb-1">{event?.name}</h1>
            <p className="text-black/70 text-sm">Application Form</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
            {/* Contact Info */}
            {hasPrefill ? (
              <p className="text-gray-400 text-sm">Hi <span className="text-white font-medium">{formData.name}</span>, please answer the questions below.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-gray-300 text-sm">Full Name *</Label>
                  <Input data-testid="app-name" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-[#0A0A0B] border-white/20 text-white h-11" placeholder="Your name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-gray-300 text-sm">Mobile *</Label>
                  <Input data-testid="app-phone" type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="bg-[#0A0A0B] border-white/20 text-white h-11" placeholder="+60 12 345 6789" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-gray-300 text-sm">Email *</Label>
                  <Input data-testid="app-email" type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="bg-[#0A0A0B] border-white/20 text-white h-11" placeholder="your@email.com" />
                </div>
              </div>
            )}

            {/* Questions */}
            <div className="space-y-5">
              {QUESTIONS.map(({ key, text }, i) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-gray-200 text-sm font-medium">
                    <span className="text-[#F5A623] mr-1.5">{i + 1}.</span>{text}
                  </Label>
                  <textarea
                    data-testid={`app-${key}`}
                    required
                    value={formData[key]}
                    onChange={(e) => setFormData({...formData, [key]: e.target.value})}
                    rows={3}
                    className="w-full bg-[#0A0A0B] border border-white/20 text-white rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#F5A623]/50 resize-y placeholder:text-gray-600"
                    placeholder="Your answer..."
                  />
                </div>
              ))}
            </div>

            {error && <p data-testid="app-error" className="text-red-400 text-sm">{error}</p>}

            <Button data-testid="app-submit-btn" type="submit" disabled={submitting} className="w-full bg-[#F5A623] hover:bg-[#E59400] text-black font-bold h-12 text-lg">
              {submitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          </form>
        </div>
        <p className="text-center text-gray-600 text-xs mt-6">Signature of Wealth by Steven Yong</p>
      </div>
    </div>
  );
}
